/**
 * The movement table: when, kind, the target, signed tenant credits, the
 * provider figure inside `CostGroup` whenever the movement carries one, then
 * who caused it. Presentational only; `TenantDetail` owns the fetch.
 *
 * QUANDO and TIPO are their own columns rather than sharing one cell, so a
 * reader can scan either alone; the two credit columns still come before the
 * actor so both figures stay inside the first 341px of a 375px viewport.
 *
 * `units.tsx`'s `CostGroup` marks the group with `role="group"` and an
 * accessible label -- that role is the marker a caller checks for, since
 * `DataTable`'s own cells carry no attribute of the table's choosing.
 */

import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { defineCatalogue, getLocale } from '../../i18n';
import { DataTable } from '../../chrome/table';
import { EmptyState } from '../../chrome/state';
import { Badge } from '../../ui/badge';
import type { OpsMovement } from '../api/schemas';
import { formatDate } from '../lib/date';
import { CostGroup, Credits } from '../lib/units';

const catalogue = defineCatalogue(
  {
    'ledger.title': 'Movimentações de créditos',
    'ledger.empty': 'Nenhuma movimentação de crédito ainda.',
    'header.when': 'Quando',
    'header.kind': 'Tipo',
    'header.credits': 'Organização',
    'header.providerCredits': 'Provedor',
    'header.target': 'Alvo',
    'header.actor': 'Responsável',
    'actor.system': 'sistema',
    'actor.noEmail': 'sem e-mail',
    'kind.GRANT': 'Crédito',
    'kind.DEBIT': 'Consumo',
    'kind.DEBIT_OVERRUN': 'Excedente',
    'kind.REFUND': 'Reembolso',
    'kind.ADJUSTMENT': 'Ajuste',
    'kind.HOLD_EXPIRY': 'Expiração',
    'target.posts': 'feed',
    'target.comments': 'comentários',
    'target.stories': 'stories',
    'target.manualGrant': 'concessão manual',
  },
  {
    'ledger.title': 'Credit movements',
    'ledger.empty': 'No credit movements yet.',
    'header.when': 'When',
    'header.kind': 'Type',
    'header.credits': 'Organisation',
    'header.providerCredits': 'Provider',
    'header.target': 'Target',
    'header.actor': 'Actor',
    'actor.system': 'system',
    'actor.noEmail': 'no email',
    'kind.GRANT': 'Credit',
    'kind.DEBIT': 'Consumption',
    'kind.DEBIT_OVERRUN': 'Overrun',
    'kind.REFUND': 'Refund',
    'kind.ADJUSTMENT': 'Adjustment',
    'kind.HOLD_EXPIRY': 'Expiry',
    'target.posts': 'feed',
    'target.comments': 'comments',
    'target.stories': 'stories',
    'target.manualGrant': 'manual grant',
  },
);

/** One label per job type the ALVO column recognises (PROVISIONAL: the
 *  frame's own words, §Task Brief). `job_type` is a bare string on the wire
 *  (`schemas.ts`'s own comment), so a type this switch does not carry falls
 *  back to its raw key rather than failing to parse or disappearing. */
function jobTypeLabel(jobType: string): string {
  switch (jobType) {
    case 'posts':
      return catalogue.t('target.posts');
    case 'comments':
      return catalogue.t('target.comments');
    case 'stories':
      return catalogue.t('target.stories');
    default:
      return jobType;
  }
}

function targetText(target: NonNullable<OpsMovement['target']>): string {
  const label = jobTypeLabel(target.job_type);
  return target.handle === null ? label : `${label} · @${target.handle}`;
}

/** One label per kind: the ledger is append-only, so an operator has to tell
 *  a grant from an adjustment from a refund. The variant is a second cue,
 *  never the only one. */
const KIND = {
  GRANT: { key: 'kind.GRANT', variant: 'default' },
  DEBIT: { key: 'kind.DEBIT', variant: 'secondary' },
  DEBIT_OVERRUN: { key: 'kind.DEBIT_OVERRUN', variant: 'secondary' },
  REFUND: { key: 'kind.REFUND', variant: 'outline' },
  ADJUSTMENT: { key: 'kind.ADJUSTMENT', variant: 'outline' },
  HOLD_EXPIRY: { key: 'kind.HOLD_EXPIRY', variant: 'outline' },
} as const satisfies Record<OpsMovement['kind'], { key: string; variant: string }>;

const COLUMNS: ColumnDef<OpsMovement, unknown>[] = [
  {
    id: 'when',
    header: () => catalogue.t('header.when'),
    cell: (info) => (
      <span className="text-caption text-muted-foreground">
        {formatDate(info.row.original.occurred_at, getLocale())}
      </span>
    ),
  },
  {
    id: 'kind',
    header: () => catalogue.t('header.kind'),
    cell: (info) => {
      const kind = KIND[info.row.original.kind];
      return (
        <Badge variant={kind.variant} data-testid="movement-kind">
          {catalogue.t(kind.key)}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'credits',
    header: () => catalogue.t('header.credits'),
    meta: { numeric: true },
    cell: (info) => (
      <span data-testid="movement-credits">
        <Credits value={info.getValue<number>()} unit="tenant" signed />
      </span>
    ),
  },
  {
    id: 'provider_credits',
    header: () => catalogue.t('header.providerCredits'),
    meta: { numeric: true },
    // null provider_credits means this movement kind carries no provider
    // cost (a grant, a refund) -- not that the cost is unpriced, so nothing
    // is rendered rather than the "sem preço" marker.
    cell: (info) => {
      const value = info.row.original.provider_credits;
      return (
        value !== null && (
          <CostGroup>
            <span data-testid="movement-provider-credits">
              <Credits value={value} unit="provider" />
            </span>
          </CostGroup>
        )
      );
    },
  },
  {
    id: 'target',
    header: () => catalogue.t('header.target'),
    // A GRANT's null target is manual (an operator typed a reason, not a
    // scraped account) and says so; every other null target has nothing to
    // point at and renders the absent state -- no placeholder, matching
    // `provider_credits` above.
    cell: (info) => {
      const { target, kind } = info.row.original;
      if (target === null) {
        return (
          kind === 'GRANT' && (
            <span data-testid="movement-target">{catalogue.t('target.manualGrant')}</span>
          )
        );
      }
      return <span data-testid="movement-target">{targetText(target)}</span>;
    },
  },
  {
    id: 'actor',
    header: () => catalogue.t('header.actor'),
    cell: (info) => {
      const { actor, note } = info.row.original;
      return (
        <div className="flex flex-col gap-1">
          <span data-testid="movement-actor">
            {actor === null
              ? catalogue.t('actor.system')
              : (actor.email ?? catalogue.t('actor.noEmail'))}
          </span>
          {note && <span className="text-caption text-muted-foreground">{note}</span>}
        </div>
      );
    },
  },
];

export function MovementLedger(props: { movements: OpsMovement[]; loading?: boolean }): ReactNode {
  return (
    <DataTable<OpsMovement>
      columns={COLUMNS}
      data={props.movements}
      loading={props.loading}
      getRowId={(row) => row.id}
      caption={catalogue.t('ledger.title')}
      emptyState={<EmptyState title={catalogue.t('ledger.empty')} />}
    />
  );
}
